import React, { useEffect, useState } from 'react';
import { Fingerprint, Loader, Lock, UserRound, WifiOff } from 'lucide-react';
import PinInput from './PinInput';
import EvalModeBanner from '../common/EvalModeBanner';
import { OfflineSession, OfflineIdentity, MAX_PIN_ATTEMPTS } from '../../services/offline/offlineSession';
import iconBlack from '../../assets/images/logofullblack.png';
import { biometricAvailable, evaluatePrf, BiometricError } from '../../services/offline/biometric';

export interface LockScreenProps {
  online: boolean;
  /** PIN accepted: the store is unlocked for this identity. */
  onUnlocked: () => void;
  /** Leave the lock screen for the password sign-in page. */
  onUsePassword: () => void;
}

/**
 * Lock screen (Phase 16A, design §6.3). Shown after the idle timeout, on relaunch, and on reload
 * for a clinician who set up offline mode on this device. No patient data is shown here.
 */
const LockScreen: React.FC<LockScreenProps> = ({ online, onUnlocked, onUsePassword }) => {
  const [identities, setIdentities] = useState<OfflineIdentity[]>(() => OfflineSession.listIdentities());
  const [selected, setSelected] = useState<OfflineIdentity | null>(() => OfflineSession.lastIdentity());
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [windowExpired, setWindowExpired] = useState(false);
  // 16D D2: fingerprint / face unlock when this identity set it up (the PIN stays the fallback)
  const [biometric, setBiometric] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!selected) return undefined;
    OfflineSession.offlineWindowHint(selected.id).then((hint) => { if (!cancelled) setWindowExpired(hint.expired); }).catch(() => undefined);
    Promise.all([OfflineSession.biometricCredential(selected.id), biometricAvailable()])
      .then(([credential, available]) => { if (!cancelled) setBiometric(Boolean(credential) && available); })
      .catch(() => setBiometric(false));
    return () => { cancelled = true; };
  }, [selected]);

  const unlockWithBiometric = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setMessage(null);
    let failure: string | null = null;
    try {
      const result = await OfflineSession.unlockWithBiometric(selected.id, async (credentialId) => {
        try {
          return await evaluatePrf(credentialId);
        } catch (e) {
          failure = e instanceof BiometricError ? e.message : null;
          throw e;
        }
      });
      if (!('reason' in result)) {
        onUnlocked();
        return;
      }
      setMessage(failure || 'Fingerprint or face did not unlock pelora. Use your PIN.');
    } finally {
      setBusy(false);
    }
  };

  const unlock = async (value: string) => {
    if (!selected || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await OfflineSession.unlock(selected.id, value);
      if (!('reason' in result)) {
        onUnlocked();
        return;
      }
      setPin('');
      if (result.reason === 'wrong_pin') {
        setMessage(`Wrong PIN. ${result.attemptsLeft} ${result.attemptsLeft === 1 ? 'try' : 'tries'} left before offline data on this device is erased.`);
      } else {
        setMessage(result.reason === 'wiped'
          ? `Too many wrong PINs (${MAX_PIN_ATTEMPTS}). Offline data for this user was erased from this device. Sign in with your password.`
          : 'Offline mode is no longer set up for this user. Sign in with your password.');
        const remaining = OfflineSession.listIdentities();
        setIdentities(remaining);
        setSelected(remaining[0] || null);
      }
    } catch {
      setMessage('Could not unlock. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const offlineBlocked = !online && windowExpired;

  return (
    <main className="flex min-h-screen items-center justify-center bg-white px-4 py-10 text-slate-800">
      <section className="w-full max-w-sm" aria-labelledby="lock-title">
        <img src={iconBlack} alt="Pelora Health" className="mx-auto mb-8 h-auto w-44 object-contain" />
        {/* Demo builds: the same disclaimer as the sign-in form (this screen replaces it on relaunch) */}
        <EvalModeBanner />
        <div className="mb-6 flex items-center justify-center gap-2">
          <Lock size={20} aria-hidden="true" />
          <h1 id="lock-title" className="text-lg font-semibold">pelora is locked</h1>
        </div>

        {!online && (
          <p className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
            <WifiOff size={16} className="shrink-0" aria-hidden="true" />You're offline. Unlock with your PIN to keep working.
          </p>
        )}

        {identities.length > 1 && (
          <fieldset className="mb-4">
            <legend className="mb-1 text-sm font-medium text-gray-700">Who is using this device?</legend>
            <div className="flex flex-wrap gap-2">
              {identities.map((identity) => (
                <button
                  key={identity.id}
                  type="button"
                  aria-pressed={selected?.id === identity.id}
                  onClick={() => { setSelected(identity); setPin(''); setMessage(null); }}
                  className={`flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-sm ${selected?.id === identity.id ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                >
                  <UserRound size={16} aria-hidden="true" />{identity.label}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {selected && !offlineBlocked && (
          <>
            {identities.length === 1 && <p className="mb-3 text-center text-sm text-gray-600">{selected.label}</p>}
            <PinInput id="unlock-pin" label="PIN" value={pin} onChange={setPin} onComplete={unlock} disabled={busy} autoFocus={!biometric} describedBy={message ? 'unlock-message' : undefined} />
            {biometric && (
              <button type="button" onClick={unlockWithBiometric} disabled={busy} className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-slate-800 text-sm font-semibold text-white disabled:opacity-50">
                <Fingerprint size={18} aria-hidden="true" />Unlock with fingerprint or face
              </button>
            )}
            {busy && <p className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-600" role="status"><Loader size={16} className="animate-spin" />Unlocking…</p>}
          </>
        )}

        {offlineBlocked && (
          <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Offline access has ended because you haven't signed in online recently. Connect to the internet and sign in with your password.
          </p>
        )}

        {message && <p id="unlock-message" role="alert" className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-800">{message}</p>}

        <button type="button" onClick={onUsePassword} disabled={!online} className="mt-6 min-h-[44px] w-full rounded-lg border border-gray-300 text-sm font-medium text-gray-700 disabled:opacity-50">
          {online ? 'Sign in with password instead' : 'Password sign-in needs a connection'}
        </button>
      </section>
    </main>
  );
};

export default LockScreen;
